export type BoundedResponseBytesResult =
  | { ok: true; bytes: Uint8Array }
  | { ok: false; reason: "payload_too_large" | "read_error" };

function declaredBodyIsTooLarge(
  contentLength: string | null,
  maxBytes: number
): boolean {
  if (!contentLength || !/^\d+$/.test(contentLength.trim())) return false;
  return Number(contentLength) > maxBytes;
}

export async function readBoundedResponseBytes(
  response: Pick<Response, "body" | "headers">,
  maxBytes: number,
  signal?: AbortSignal
): Promise<BoundedResponseBytesResult> {
  if (!Number.isSafeInteger(maxBytes) || maxBytes <= 0) {
    throw new RangeError("maxBytes must be a positive safe integer");
  }

  if (
    declaredBodyIsTooLarge(response.headers.get("content-length"), maxBytes)
  ) {
    void response.body?.cancel().catch(() => undefined);
    return { ok: false, reason: "payload_too_large" };
  }

  if (!response.body) return { ok: true, bytes: new Uint8Array() };

  if (signal?.aborted) {
    void response.body.cancel().catch(() => undefined);
    return { ok: false, reason: "read_error" };
  }
  if (response.body.locked) return { ok: false, reason: "read_error" };
  const reader = response.body.getReader();
  const abort = () => {
    // Cancellation closes pending reads immediately, even if the source cleanup stalls.
    void reader.cancel().catch(() => undefined);
  };
  signal?.addEventListener("abort", abort, { once: true });
  const chunks: Uint8Array[] = [];
  let receivedBytes = 0;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (signal?.aborted) return { ok: false, reason: "read_error" };
      if (done) break;
      receivedBytes += value.byteLength;
      if (receivedBytes > maxBytes) {
        void reader.cancel().catch(() => undefined);
        return { ok: false, reason: "payload_too_large" };
      }
      chunks.push(value);
    }

    const bytes = new Uint8Array(receivedBytes);
    let offset = 0;
    for (const chunk of chunks) {
      bytes.set(chunk, offset);
      offset += chunk.byteLength;
    }
    return { ok: true, bytes };
  } catch {
    return { ok: false, reason: "read_error" };
  } finally {
    signal?.removeEventListener("abort", abort);
    reader.releaseLock();
  }
}
